/**
 * Utility for tracking recent form submissions to prevent duplicates
 */

interface SubmissionRecord {
  timestamp: number;
  data: string; // Hash or stringified data
}

class SubmissionTracker {
  private submissions: Map<string, SubmissionRecord> = new Map();
  private readonly timeWindowMs: number;

  constructor(timeWindowMs: number = 5000) {
    this.timeWindowMs = timeWindowMs;
  }

  /**
   * Check if a similar submission was made recently
   * @param key Unique identifier for the submission type
   * @param data The data being submitted
   * @returns true if a duplicate submission was detected
   */
  isDuplicateSubmission(key: string, data: any): boolean {
    const now = Date.now();
    const dataString = JSON.stringify(data);
    
    // Clean up old submissions
    this.cleanupOldSubmissions();
    
    const existingSubmission = this.submissions.get(key);
    
    if (existingSubmission) {
      const timeSinceLastSubmission = now - existingSubmission.timestamp;
      
      // Check if it's within the time window and has the same data
      if (timeSinceLastSubmission < this.timeWindowMs && existingSubmission.data === dataString) {
        return true;
      }
    }
    
    // Record this submission
    this.submissions.set(key, {
      timestamp: now,
      data: dataString
    });
    
    return false;
  }

  /**
   * Clear a submission record (useful after successful submission)
   */
  clearSubmission(key: string): void {
    this.submissions.delete(key);
  }

  /**
   * Clean up submissions older than the time window
   */
  private cleanupOldSubmissions(): void {
    const now = Date.now();
    const cutoffTime = now - this.timeWindowMs;
    
    for (const [key, record] of this.submissions.entries()) {
      if (record.timestamp < cutoffTime) {
        this.submissions.delete(key);
      }
    }
  }
}

// Export a singleton instance with a 5-second window
export const submissionTracker = new SubmissionTracker(5000);